import React, { useState, useEffect, useCallback, useRef } from 'react';
import {PlusCircle, Save, X, Shield, Briefcase, UserCheck, Info, Search, Check} from 'lucide-react';
import server from '../../../api/server';
import { useToast } from '../../../context/ToastContext.jsx';
import '../../../dashboard-components/loader.css';
import UnauthorizedPage from '../../layouts/UnauthorizedPage';
import { FaTimes } from 'react-icons/fa';
import ModernTimeDropdown from '../../layouts/modernDropdown';

const OrganizationSettingsPage = ({serviceID, clientID}) => {
  const { showToast } = useToast();
  const activeClientIdRef = useRef(clientID);
  activeClientIdRef.current = clientID;
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('All')
  // States
  const [orgName, setOrgName] = useState('');
  const [savedOrgName, setSavedOrgName] = useState('');
  const [projectMetadata, setProjectMetadata] = useState([]);
  const [projectNameDrafts, setProjectNameDrafts] = useState({});
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [savingDisplayName, setSavingDisplayName] = useState(null);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [selectedRole, setSelectedRole] = useState('Employee');
  const [email, setEmail] = useState(null)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [teamMembers, setTeamMembers] = useState({ admins: [], users: [] });
  const [name, setName] = useState(null)
  const [useremail, setUseremail] = useState(null)
  const [formloading, setformloading] = useState(false)
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [toDeleteObj, setToDeleteObj] = useState(null)
  const [projectsAccess, setProjectsAccess] = useState([])
  const [allProjects, setAllProjects] = useState([])
  const [isLoading, setIsLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false)
  const [employeesList, setEmployeesList] = useState([])
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([])
  const [filteredAdmins, setFilteredAdmins] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const clientSwitchPending = clientID !== client_id;
  const [role] = useState(() => {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const foundRole = userData.role
      return foundRole;
    }
    return 'admin';
  })

  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }

  const getTeamMembersData = async() => {
    try {
      if(role.toLowerCase() === 'employee'){
        return;
      }
      setIsLoading(true)
      let res = await server.getTeamMemberSettings(client_id, service_id, email, 'organization-settings', undefined)
      if(res.status === 200){
        let resp = await res.json()
        setTeamMembers(resp.data)
        // Don't set filtered arrays here - let applyFilters handle it
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
    }
  }
  const getProjectsList = async () => {
    try {
      if(role.toLowerCase() === 'employee'){
        return;
      }
      let res = await server.getProjectsList(client_id, service_id, email, 'organization-settings', undefined, role)
      if(res.status === 200){
        let resp = await res.json()
        if(resp.data){
          setAllProjects(resp.data)
        }
      }
    } catch (error) {
    }
  }
  const getOrganizationMetadata = useCallback(async () => {
    const requestClientId = client_id;
    if (activeClientIdRef.current !== requestClientId) return;
    try {
      setMetadataLoading(true);
      setOrgName('');
      setSavedOrgName('');
      setProjectMetadata([]);
      setProjectNameDrafts({});
      const res = await server.getOrganizationMetadata(requestClientId, email);
      const resp = await res.json();
      if (activeClientIdRef.current !== requestClientId) return;
      if (res.status === 200 && resp.data) {
        setOrgName(resp.data.organisation_name || '');
        setSavedOrgName(resp.data.organisation_name || '');
        const projects = Array.isArray(resp.data.projects) ? resp.data.projects : [];
        setProjectMetadata(projects);
        setProjectNameDrafts(Object.fromEntries(
          projects.map((project) => [project.service_id, project.project_name || ''])
        ));
      } else {
        showToast(resp.error || 'Unable to load organization details.', 'error');
      }
    } catch (error) {
      if (activeClientIdRef.current === requestClientId) {
        showToast('Unable to load organization details.', 'error');
      }
    } finally {
      if (activeClientIdRef.current === requestClientId) {
        setMetadataLoading(false);
      }
    }
  }, [client_id, email, showToast]);
  const saveOrganizationName = async () => {
    if (clientSwitchPending) return;
    if (!orgName.trim()) {
      showToast('Organization name is required.', 'error');
      return;
    }
    const requestClientId = client_id;
    const submittedName = orgName;
    try {
      setSavingDisplayName('organization');
      const res = await server.updateOrganizationMetadata({
        entity_type: 'organization',
        name: submittedName,
        client_id: requestClientId,
        email,
      });
      const resp = await res.json();
      if (activeClientIdRef.current !== requestClientId) return;
      if (res.status === 200) {
        const canonicalName = resp.data.organisation_name;
        setOrgName(canonicalName);
        setSavedOrgName(canonicalName);
        window.dispatchEvent(new CustomEvent('displayNamesUpdated', {
          detail: {
            entity_type: 'organization',
            client_id: requestClientId,
            name: canonicalName,
          },
        }));
        showToast('Organization name updated successfully.', 'success');
      } else {
        showToast(resp.error || 'Unable to update organization name.', 'error');
      }
    } catch (error) {
      if (activeClientIdRef.current === requestClientId) {
        showToast('Unable to update organization name.', 'error');
      }
    } finally {
      if (activeClientIdRef.current === requestClientId) {
        setSavingDisplayName(null);
      }
    }
  }
  const saveProjectName = async (serviceId) => {
    if (clientSwitchPending) return;
    const projectName = projectNameDrafts[serviceId] || '';
    if (!projectName.trim()) {
      showToast('Project name is required.', 'error');
      return;
    }
    const requestClientId = client_id;
    try {
      setSavingDisplayName(serviceId);
      const res = await server.updateOrganizationMetadata({
        entity_type: 'project',
        service_id: serviceId,
        name: projectName,
        client_id: requestClientId,
        email,
      });
      const resp = await res.json();
      if (activeClientIdRef.current !== requestClientId) return;
      if (res.status === 200) {
        const canonicalName = resp.data.project_name;
        setProjectMetadata((projects) => projects.map((project) => (
          project.service_id === serviceId
            ? { ...project, project_name: canonicalName }
            : project
        )));
        setProjectNameDrafts((drafts) => ({ ...drafts, [serviceId]: canonicalName }));
        window.dispatchEvent(new CustomEvent('displayNamesUpdated', {
          detail: {
            entity_type: 'project',
            client_id: requestClientId,
            service_id: serviceId,
            name: canonicalName,
          },
        }));
        showToast('Project name updated successfully.', 'success');
      } else {
        showToast(resp.error || 'Unable to update project name.', 'error');
      }
    } catch (error) {
      if (activeClientIdRef.current === requestClientId) {
        showToast('Unable to update project name.', 'error');
      }
    } finally {
      if (activeClientIdRef.current === requestClientId) {
        setSavingDisplayName(null);
      }
    }
  }
  const handleAddMember = async () => {
    try {
      setformloading(true)
      if(!isValidEmail(useremail)){
        showToast('Invalid email!', "error")
        setformloading(false)
        return;
      }
      let res = null
      let obj = {useremail, email, 'user_role': selectedRole.toLowerCase(), 'project_access': projectsAccess, name, client_id, service_id, 'menu': 'organization-settings', 'submenu': undefined}
      if(isEdit){
        res = await server.editTeamMemberSettings(obj)
      }else{
        res = await server.postTeamMemberSettings(obj);
      }
      let resp = await res.json()
      if(res.status === 200){
        if(isEdit){
          showToast("Used Modified Successfully!", "success")
        }else{
          showToast('User Added Successfully!', "success")
        }
        setIsEdit(false)
        setUseremail(null)
        setSelectedRole('Employee')
        setProjectsAccess([])
        setName('')
        getTeamMembersData()
      }else{
        showToast(resp.error, "error")
      }
      setformloading(false)
      setShowAddMemberForm(false);
    } catch (error) {
      setformloading(false)
    }
  }
  const handleSearch = (value) => {
    setSearch(value)
    applyFilters(value, roleFilter)
  }
  
  const handleRoleFilter = (value) => {
    setRoleFilter(value)
    applyFilters(search, value)
  }
  
  const applyFilters = (searchValue, roleValue) => {
    let filteredAdminsArray = teamMembers.admins || [];
    let filteredUsersArray = teamMembers.users || [];
    
    // Apply search filter to both admins and users
    if (searchValue && searchValue.trim() !== '') {
      filteredAdminsArray = filteredAdminsArray.filter((member) => 
        member.name.toLowerCase().includes(searchValue.toLowerCase()) || 
        member.email.toLowerCase().includes(searchValue.toLowerCase())
      );
      filteredUsersArray = filteredUsersArray.filter((member) => 
        member.name.toLowerCase().includes(searchValue.toLowerCase()) || 
        member.email.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Apply role filter
    if (roleValue && roleValue !== 'All') {
      if (roleValue.toLowerCase() === 'admin') {
        // Show only admins, hide users
        filteredUsersArray = [];
      } else {
        // Show only users with the selected role, hide admins
        filteredAdminsArray = [];
        filteredUsersArray = filteredUsersArray.filter((member) => 
          member.role.toLowerCase() === roleValue.toLowerCase()
        );
      }
    }
    
    setFilteredAdmins(filteredAdminsArray);
    setFilteredUsers(filteredUsersArray);
  }
  const hitDeleteApi = async () => {
    try {
      setIsLoading(true)
      let res = await server.deleteTeamMemberSettings(toDeleteObj)
      if(res.status === 200){
        getTeamMembersData()
        showToast('User deleted successfully.', "success")
        setToDeleteObj(null)
      }
      setIsLoading(false)
      setShowConfirmBox(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  const deleteMember = async(useremail) => {
      setShowConfirmBox(true)
      let obj = {useremail, email, client_id, service_id, menu: 'organization-settings', submenu: undefined}
      setToDeleteObj(obj)
  }

  const handleProjectAccessChange = (project) => {
    setProjectsAccess((prevAccess) => {
      // Ensure prevAccess is always an array
      const accessArray = Array.isArray(prevAccess) ? prevAccess : [];
      
      if (accessArray.includes(project)) {
        return accessArray.filter((p) => p !== project);
      } else {
        return [...accessArray, project];
      }
    });
  };

  // Add useEffect to handle role changes
  useEffect(() => {
    if (selectedRole === 'Admin') {
      // For admins, always set access to all projects
      setProjectsAccess([...allProjects]);
    } else if (selectedRole && !isEdit) {
      // For new non-admin users, reset to empty array
      setProjectsAccess([]);
    }
    // Note: For editing non-admin roles, we don't automatically reset projectsAccess
    // as it was already set by normalizeProjectAccess when the edit button was clicked
  }, [selectedRole, allProjects]);

  // Add function to normalize project access when editing
  const normalizeProjectAccess = (projects) => {
    if (!projects) return [];
    if (Array.isArray(projects)) return projects;
    if (typeof projects === 'string') {
      // Handle cases where projects might be stored as a string like "All" or comma-separated
      if (projects.toLowerCase() === 'all' || projects.toLowerCase().includes('all')) {
        return allProjects;
      }
      // Try to split if it's comma-separated
      return projects.split(',').map(p => p.trim()).filter(p => p);
    }
    return [];
  };
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user) {
      setEmail(user.email);
    }
    if (serviceID) {
      setService_id(serviceID);
    }
    if(clientID){
      setClient_id(clientID);
      setSavingDisplayName(null);
    }
  }, [serviceID, clientID]);
  const getEmployeesList = async () => {
    try {
      let res = await server.getAdvancedFilterDetailsAgentFeedback(client_id, service_id, email, 'organization-settings', undefined)
      if(res.status === 200){
        let resp = await res.json()
        setEmployeesList(resp.employee_id)
      }
    } catch (error) {
    }
  }
  

  useEffect(() => {
    if (client_id && service_id && email && (role !== 'employee' && role!=='manager')) {
      getTeamMembersData();
      getProjectsList();
      getEmployeesList();
    }
  }, [client_id, service_id, email]);

  useEffect(() => {
    if (client_id && email && (role !== 'employee' && role !== 'manager')) {
      getOrganizationMetadata();
    }
  }, [client_id, email, role, getOrganizationMetadata]);
  
  // Reapply filters when teamMembers changes
  useEffect(() => {
    if (teamMembers.admins || teamMembers.users) {
      applyFilters(search, roleFilter);
    }
  }, [teamMembers]);
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowAddMemberForm(false);
      }
    }
    if (showAddMemberForm) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    }
  }, [showAddMemberForm])

  return (
    <div className="flex flex-col bg-white overflow-hidden font-sans">
      {/* Main Content */}
      {(role !== 'employee' && role !== 'manager') && <div className="flex flex-1 overflow-hidden">
        
        {/* Content */}
        <main className="flex-1 py-3 px-5 bg-gray-50 overflow-y-auto overflow-x-hidden">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Organization Settings</h1>
            <p className="text-sm text-gray-500">Manage your organization details, roles, and team members</p>
          </div>
          
          {/* Organization Details Section */}
          <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Organization Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
                <input
                  type="text"
                  value={client_id || ''}
                  disabled
                  className="bg-gray-100 pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Permanent ID</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    maxLength={255}
                    value={orgName}
                    disabled={clientSwitchPending || metadataLoading || savingDisplayName === 'organization'}
                    onChange={(event) => setOrgName(event.target.value.toUpperCase())}
                    className="global-input pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={saveOrganizationName}
                    disabled={clientSwitchPending || metadataLoading || savingDisplayName !== null || !orgName.trim() || orgName === savedOrgName}
                    className="flex items-center bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Required · Unique</p>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-medium text-gray-900">Projects</h3>
                <span className="text-xs text-gray-500">Names must be unique</span>
              </div>
              <div className="max-h-72 overflow-auto border border-gray-200 rounded-md">
                <table className="min-w-[640px] w-full table-fixed divide-y divide-gray-200">
                  <thead className="sticky top-0 z-10 bg-gray-50">
                    <tr>
                      <th scope="col" className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Name</th>
                      <th scope="col" className="w-28 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projectMetadata.map((project) => {
                      const draftName = projectNameDrafts[project.service_id] || '';
                      const unchanged = draftName === project.project_name;
                      return (
                        <tr key={project.service_id}>
                          <td className="px-6 py-2 text-sm text-gray-700">
                            <span className="block font-mono truncate" title={project.service_id}>{project.service_id}</span>
                          </td>
                          <td className="px-6 py-2">
                            <input
                              type="text"
                              required
                              maxLength={255}
                              value={draftName}
                              disabled={clientSwitchPending || metadataLoading || savingDisplayName === project.service_id}
                              onChange={(event) => setProjectNameDrafts((drafts) => ({
                                ...drafts,
                                [project.service_id]: event.target.value.toUpperCase(),
                              }))}
                              className="global-input pl-3 pr-4 py-1.5 border border-gray-300 rounded-md w-full text-sm"
                            />
                          </td>
                          <td className="px-6 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => saveProjectName(project.service_id)}
                              disabled={clientSwitchPending || metadataLoading || savingDisplayName !== null || !draftName.trim() || unchanged}
                              className="inline-flex items-center bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!metadataLoading && projectMetadata.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-6 py-6 text-center text-sm text-gray-500">No projects found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* Role Management Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Role Management</h2>
              <button 
                onClick={() => setShowAddMemberForm(true)}
                className="flex items-center bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Team Member
              </button>
            </div>
            
            {/* Role Description Cards */}
            <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-200">
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <Shield className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Admin</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Access to everything on the dashboard</li>
                    <li>Can add admins, project owners & team members</li>
                    <li>Access to organization settings</li>
                  </ul>
                </div>
                
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <Briefcase className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Manager</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Project level access</li>
                    <li>Can add team members</li>
                    <li>Can have access to multiple projects</li>
                    <li>No access to organization settings</li>
                  </ul>
                </div>
                
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <UserCheck className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Employee</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Limited access</li>
                    <li>Access to their own data</li>
                    <li>No management capabilities</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Team Members Search and Filter */}
            <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-4 justify-between">
                <div className="relative w-2/3">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                    value={search}
                    onChange={(e)=>handleSearch(e.target.value)}
                  />
                </div>
                
                <div className="w-1/3">
                  <ModernTimeDropdown value={roleFilter} onChange={(e)=>handleRoleFilter(e.target.value)} options={['All', 'Admin', 'Manager', 'Employee']} width="w-full"/>
                </div>
              </div>
            </div>
            
            {/* Team Members Table */}
            <div className="overflow-hidden border border-gray-200 rounded-lg">
            {
              isLoading && (
                <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )
            }
              {!isLoading && 
              <div className="max-h-80 overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-9">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdmins?.map((admin) => (
                    <tr key={admin.email} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center text-gray-700 mr-3">
                            {admin.name.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{admin.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Shield className="h-4 w-4 text-indigo-500 mr-2" />
                          <span className="text-sm text-gray-900 capitalize">{admin.role}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(admin.projects) ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {admin.projects.join(', ')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {admin.projects}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                        {email !== admin.email ? (
                          <>
                            <button
                              onClick={() => {
                                setShowAddMemberForm(true);
                                setName(admin.name);
                                setUseremail(admin.email);
                                setSelectedRole(admin.role);
                                setProjectsAccess(normalizeProjectAccess(admin.projects));
                                setIsEdit(true);
                              }}
                              className="hover:underline text-indigo-600"
                            >
                              Edit
                            </button>
                            <span className="mx-2 text-gray-300">|</span>
                            <button onClick={() => deleteMember(admin.email)} className="hover:underline text-red-600">
                              Remove
                            </button>
                          </>
                        ) : (
                          <div className="flex items-center text-gray-400">
                                                      <Info className="h-3 w-3 mr-1" />
                                                      <span>Cannot modify</span>
                                                    </div>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers?.map((user) => (
                    <tr key={user.email} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center text-gray-700 mr-3">
                            {user.name.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserCheck className="h-4 w-4 text-indigo-500 mr-2" />
                          <span className="text-sm text-gray-900 capitalize">{user.role}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(user.projects) ? (
                            <>{user.projects.map((proj, idx)=> {return <span key={`${proj}-${idx}`} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 mr-2">
                              {proj}
                            </span>})}</>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {user.projects}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                        {email !== user.email ? (
                            <>
                              <button
                                onClick={() => {
                                  setShowAddMemberForm(true);
                                  setName(user.name);
                                  setUseremail(user.email);
                                  setSelectedRole(user.role);
                                  setProjectsAccess(normalizeProjectAccess(user.projects));
                                  setIsEdit(true);
                                }}
                                className="hover:underline text-indigo-600"
                              >
                                Edit
                              </button>
                              <span className="mx-2 text-gray-300">|</span>
                              <button onClick={() => deleteMember(user.email)} className="hover:underline text-red-600">
                                Remove
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center text-gray-400">
                                                        <Info className="h-3 w-3 mr-1" />
                                                        <span>Cannot modify</span>
                                                      </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              }
            </div>
          </div>
          
          {/* Add Team Member Form (Modal) */}
          {showAddMemberForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">{isEdit ? 'Edit' : 'Add'} Team Member</h3>
                  <button 
                    onClick={() => {
                      setShowAddMemberForm(false); 
                      setName('');
                      setUseremail(null);
                      setSelectedRole('Employee');
                      setProjectsAccess([]);
                      setIsEdit(false);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                {!formloading && <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <ModernTimeDropdown width="w-full" placeholder="Select Role" value={selectedRole} onChange={(e)=>setSelectedRole(e.target.value)} options={['Admin', 'Manager', 'Employee']}/>
                    {selectedRole === 'Admin' && (
                      <p className="mt-1 text-xs text-indigo-600 flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        Admins have access to all projects and settings
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    {selectedRole !== 'Employee' && <input class="global-input"
                      type="text" 
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                      placeholder="Enter full name" value={name} onChange={(e)=> setName(e.target.value)}
                    />}
                    {selectedRole === 'Employee' && <ModernTimeDropdown width="w-full" placeholder="Select Employee" value={name} onChange={(e)=>setName(e.target.value)} options={employeesList}/>}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email ID</label>
                    <input class="global-input"
                      type="email" 
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                      placeholder="Enter email address" value={useremail} onChange={(e)=> setUseremail(e.target.value.toLowerCase())} disabled={isEdit}
                    />
                  </div>
                  
                  
                  {selectedRole !== 'Admin' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Projects Access</label>
                      <div className="bg-white border border-gray-300 rounded-md">
                        <div className="p-2 border-b border-gray-200 bg-gray-50">
                          <div className="flex items-center">
                            <input class="global-input"
                              type="checkbox"
                              id="select-all"
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              checked={Array.isArray(projectsAccess) && projectsAccess.length === allProjects.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setProjectsAccess([...allProjects]);
                                } else {
                                  setProjectsAccess([]);
                                }
                              }}
                            />
                            <label htmlFor="select-all" className="ml-2 text-sm font-medium text-gray-700">Select All</label>
                          </div>
                        </div>
                        <div className="p-2 max-h-40 overflow-y-auto">
                          <div className="space-y-2">
                            {
                              allProjects.map((item, idx)=> {
                                return (<div className="flex items-center p-2 hover:bg-gray-50 rounded" key={idx}>
                                  <input class="global-input"
                                    type="checkbox"
                                    id={`project-${idx}`}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    checked={Array.isArray(projectsAccess) && projectsAccess.includes(item)}
                                    onChange={() => handleProjectAccessChange(item)}
                                  />
                                  <label htmlFor={`project-${idx}`} className="ml-2 text-sm text-gray-700">{item}</label>
                                </div>)
                              })
                            }
                          </div>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {selectedRole === 'Manager' ? 'Managers can access and manage selected projects' : 'Employees can only view data for selected projects'}
                      </p>
                    </div>
                  )}
                </div>}
                {
                  formloading && (<div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                    <div className="loader"></div>
                    <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                  </div>)
                  
                }
                
                <div className="flex justify-end p-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAddMemberForm(false); 
                      setName('');
                      setUseremail(null);
                      setSelectedRole('Employee');
                      setProjectsAccess([]);
                      setIsEdit(false);
                    }}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleAddMember()
                    }}
                    className={`bg-indigo-600 text-white px-4 py-2 rounded text-sm ${formloading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isEdit ? 'Update Member' : 'Add Member'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="fixed bottom-4 right-4 bg-green-50 border border-green-300 text-green-800 rounded-md px-4 py-3 shadow-md z-50 flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <p className="font-medium">Success!</p>
                <p className="text-sm">Team member has been added successfully</p>
              </div>
              <button 
                onClick={() => setShowSuccessMessage(false)}
                className="ml-4 text-green-500"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>
          )}
          {
            showConfirmBox && (
              <div style={{zIndex: "9999999999", position: "fixed", top: "0", left: "0", height: "100%", width: "100%", background: "rgba(0,0,0, 0.5)"}}>
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Are you sure?</h2>
                  <button 
                    onClick={() => {
                      {setShowConfirmBox(false); setToDeleteObj(null);}
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                  <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                <div className="p-6 pt-2">
                  <p className="text-gray-500 text-sm">Team member will lose access to all the projects.</p>
                </div>
                
                <div className="flex justify-end gap-3 p-6 pt-0">
                  <button
                    onClick={() => {setShowConfirmBox(false); setToDeleteObj(null);}}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                  >
                    No
                  </button>
                  <button onClick={hitDeleteApi}
                    className="px-6 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
        </div>
            )
          }
        </main>
      </div>}
      { (role === 'employee' || role === 'manager') && (
      <UnauthorizedPage/>)
      }
    </div>
  );
};

export default OrganizationSettingsPage;
